import './HomeReportsAndNews.styles.scss';
import { useHomeReportsAndNews } from "@apiHooks/useHomeReportsAndNews";
import HomeSectionHeader from "@pages/Home/components/HomeSectionHeader";
import HomeSectionTitle from "@pages/Home/components/HomeSectionTitle";
import HomeSectionLoader from "@pages/Home/components/HomeSectionLoader";
import Loader from "@components/new/Loader/Loader";
import HomeSectionDelimiter from "@pages/Home/components/HomeSectionDelimiter";
import HomeSectionFooter from "@pages/Home/components/HomeSectionFooter";
import AccessLink from "@components/AccessLink/AccessLink";
import cn from "classnames";
import { AppId, FCX, usePlatformContext, usePlatformFeatureFlag } from "@datassential/platform-ui-lib";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import HomeReports from "@pages/Home/sections/HomeReportsAndNews/HomeReports";
import HomeSectionTabs from "@pages/Home/components/HomeSectionTabs";
import HomeNews from "@pages/Home/sections/HomeReportsAndNews/HomeNews";
import HomeReportsTagsFilter from "@pages/Home/sections/HomeReportsAndNews/HomeReportsTagsFilter";
import useLocalStorageState from "@hooks/useLocalStorageState";
import { AppContext } from "../../../../AppContext";
import { IoChevronForward, IoChevronBack } from "react-icons/io5";
import { sanitizeUrl } from "@helpers";
import { useHomeWorkspace } from "@pages/Home/context/HomeWorkspaceContext";

const HomeReportsAndNews: FCX = () => {
    const isNewsPersonalizationEnabled = usePlatformFeatureFlag('News personalization');
    const { globalVariables } = useContext(AppContext);
    const availableTags = globalVariables.allTags;
    const { userPreferenceTags } = globalVariables;

    const [selectedTagIdsState, setSelectedTagIdsState] = useState<number[] | null>(null);
    const selectedTagIds = useMemo<number[]>(
        () => {
            if (!isNewsPersonalizationEnabled || availableTags.length === 0) return [];
            const availableIds = new Set(availableTags.map(t => t.id));
            if (selectedTagIdsState === null) {
                return userPreferenceTags
                    .map(t => t.id)
                    .filter(id => availableIds.has(id));
            }
            return selectedTagIdsState.filter(id => availableIds.has(id));
        },
        [isNewsPersonalizationEnabled, selectedTagIdsState, availableTags, userPreferenceTags]
    );

    const setSelectedTagIds = useCallback(
        (ids: number[]) => setSelectedTagIdsState(ids),
        []
    );

    const { isLoading, data: rawData } = useHomeReportsAndNews(selectedTagIds);
    const [stableData, setStableData] = useState(rawData);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    useEffect(() => {
        if (!isLoading) {
            setStableData(rawData);
            setHasLoadedOnce(true);
        }
    }, [isLoading, rawData]);

    const showFullLoader = isLoading && !hasLoadedOnce;
    const { markReady } = useHomeWorkspace();
    const [selectedTab, setSelectedTab] = useLocalStorageState<'reports' | 'news'>('home-reports-news-tab','news');
    const reportsPublications = useMemo(() => stableData.publications ?? [], [stableData]);
    const isReportsEmpty = selectedTab === 'reports' && selectedTagIds.length > 0 && reportsPublications.length === 0;
    const [frontBgSrc, setFrontBgSrc] = useState('');
    const [backBgSrc, setBackBgSrc] = useState('');
    const [visibleBg, setVisibleBg] = useState<'front' | 'back'>('front');
    const navHandlersRef = useRef<{ next: () => void; prev: () => void } | null>(null);

    const {
        userInfo: {
            products,
        },
    } = usePlatformContext();

    const reportProProduct = useMemo(
        () => products[AppId.ReportPro],
        [products]
    );

    const preloadedImages = useRef<Set<string>>(new Set());

    useEffect(() => {
        if (!isLoading) markReady();
    }, [isLoading, markReady]);

    // Preload all card images into browser cache as soon as data arrives
    useEffect(() => {
        if (isLoading) return;
        const allImages = [
            ...(stableData.news ?? []).map(i => i.featuredImage),
            ...(stableData.publications ?? []).map(i => i.imageUrl),
        ].filter(Boolean) as string[];

        allImages.forEach(src => {
            if (preloadedImages.current.has(src)) return;
            const img = new Image();
            img.onload = () => preloadedImages.current.add(src);
            img.src = src;
        });
    }, [isLoading, stableData]);

    useEffect(() => {
        if (isReportsEmpty) {
            setFrontBgSrc('');
            setBackBgSrc('');
        }
    }, [isReportsEmpty]);

    const handleBgChange = useCallback((newSrc: string) => {
        if (!newSrc) return;

        const applyCrossfade = () => {
            // Set new image on the hidden bg, then crossfade
            setVisibleBg(currentVisible => {
                if (currentVisible === 'front') {
                    setBackBgSrc(newSrc);
                } else {
                    setFrontBgSrc(newSrc);
                }
                // Wait 2 frames for the hidden bg to paint, then switch
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        setVisibleBg(currentVisible === 'front' ? 'back' : 'front');
                    });
                });
                return currentVisible;
            });
        };

        // If already preloaded, apply immediately
        if (preloadedImages.current.has(newSrc)) {
            applyCrossfade();
            return;
        }

        // Otherwise preload first, then apply (current bg stays visible while loading)
        const img = new Image();
        img.onload = () => {
            preloadedImages.current.add(newSrc);
            applyCrossfade();
        };
        img.src = newSrc;
    }, []);

    const handleNavReady = useCallback((handlers: { next: () => void; prev: () => void }) => {
        navHandlersRef.current = handlers;
    }, []);

    const handleNext = useCallback(() => {
        navHandlersRef.current?.next();
    }, []);

    const handlePrev = useCallback(() => {
        navHandlersRef.current?.prev();
    }, []);

    const seeMoreLink = selectedTab === 'news'
        ? { label: 'All News', url: sanitizeUrl(`${reportProProduct.url}/news`) }
        : { label: 'See More Reports', url: sanitizeUrl(reportProProduct.url) };

    return (
        <>
            <div
                className={cn(
                    "HomeReportsAndNews",
                    showFullLoader && "is-loading",
                )}
            >
                <div
                    className={cn(
                        "HomeReportsAndNews__bg",
                        "HomeReportsAndNews__bg--front",
                        visibleBg === 'front' && "is-visible",
                    )}
                    style={{ backgroundImage: frontBgSrc ? `url(${frontBgSrc})` : undefined }}
                />
                <div
                    className={cn(
                        "HomeReportsAndNews__bg",
                        "HomeReportsAndNews__bg--back",
                        visibleBg === 'back' && "is-visible",
                    )}
                    style={{ backgroundImage: backBgSrc ? `url(${backBgSrc})` : undefined }}
                />
                <div className="HomeReportsAndNews__overlay"/>
                <div className="HomeReportsAndNews__content">
                    <div className="HomeReportsAndNews__content-wrapper">
                        {!showFullLoader && (
                            <div>
                                <div
                                    className="Home__wrapper"
                                    style={{
                                        paddingBottom: 48,
                                        paddingTop: 60,
                                        paddingLeft: 70,
                                        paddingRight: 70,
                                    }}
                                >
                                    <HomeSectionHeader>
                                        <HomeSectionTitle>
                                            News & Reports
                                        </HomeSectionTitle>
                                        <HomeSectionTabs
                                            data={[
                                                { id: 'news', title: 'News' },
                                                { id: 'reports', title: 'Reports' },
                                            ]}
                                            activeTabId={selectedTab}
                                            onItemClick={(id) => setSelectedTab(id as 'reports' | 'news')}
                                        />
                                    </HomeSectionHeader>
                                    <div className="HomeReportsAndNews__nav-row">
                                        <div className="HomeReportsAndNews__nav-row-filter">
                                            {isNewsPersonalizationEnabled && availableTags.length > 0 && (
                                                <HomeReportsTagsFilter
                                                    availableTags={availableTags}
                                                    selectedTagIds={selectedTagIds}
                                                    setSelectedTagIds={setSelectedTagIds}
                                                />
                                            )}
                                        </div>
                                        <div className="HomeReportsAndNews__nav-buttons">
                                            <button
                                                className="HomeReportsAndNews__nav-btn"
                                                onClick={handlePrev}
                                                aria-label="Previous"
                                            >
                                                <IoChevronBack size={20} />
                                            </button>
                                            <button
                                                className="HomeReportsAndNews__nav-btn"
                                                onClick={handleNext}
                                                aria-label="Next"
                                            >
                                                <IoChevronForward size={20} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    className={cn(
                                        "HomeReportsAndNews__carousel-wrap",
                                        isLoading && hasLoadedOnce && "is-loading",
                                    )}
                                >
                                    {isLoading && hasLoadedOnce && (
                                        <Loader className="HomeReportsAndNews__loader" />
                                    )}
                                    <div className="Home__wrapper" style={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 70, paddingRight: 70 }}>
                                        {selectedTab === 'news' && (
                                            <HomeNews
                                                data={stableData.news ?? []}
                                                setBgSrc={handleBgChange}
                                                onNavReady={handleNavReady}
                                            />
                                        )}
                                        {selectedTab === 'reports' && (
                                            isReportsEmpty ? (
                                                <div className="HomeReportsAndNews__empty">
                                                    No publications found per applied tags.
                                                </div>
                                            ) : (
                                                <HomeReports
                                                    data={reportsPublications}
                                                    setBgSrc={handleBgChange}
                                                    onNavReady={handleNavReady}
                                                />
                                            )
                                        )}
                                    </div>
                                </div>
                                <div className="Home__wrapper" style={{ paddingLeft: 70, paddingRight: 70 }}>
                                    <HomeSectionFooter>
                                        <AccessLink
                                            url={seeMoreLink.url}
                                            text={seeMoreLink.label}
                                            onContactUsClick={() => {}}
                                        />
                                    </HomeSectionFooter>
                                </div>
                            </div>
                        )}
                        {showFullLoader && <HomeSectionLoader/>}
                    </div>
                </div>
            </div>
            <div className="Home__wrapper">
                <HomeSectionDelimiter colors={['#1DFEEC', '#4FB0F9', '#FF59B9', '#FF0395']}/>
            </div>
        </>
    );
}

export default HomeReportsAndNews;
